import { Link } from "react-router-dom";
import { Link as LinkComponent } from "../../components/atoms/Link";
import styled from "styled-components";
import leftImg from "../../assets/images/left-image-landing-page.jpg";
import rightImg from "../../assets/images/right-image-landing-page.jpg";
import appConfig from "../../lib/appConfig";
import colours from "../../lib/colours";
import { ReactComponent as ArrowIcon } from "../../assets/svg/arrow-right-long.svg";
import { ExternalLinkIcon } from "../../components/atoms/ExternalLinkIcon";
import { IProps as DialogProps } from "../../components/organisms/Dialog";
import DialogContainer, { initialDialogProps } from "../Other/DialogContainer";
import ContextProvider, {
  useDialogContext,
} from "../../context/ContextProvider";
import TextButton from "../../components/atoms/TextButton";
import LandingPageHelp from "../Other/LandingPageHelp";
import Footer from "../Other/Footer";
import { applicantsGuide } from "../../lib/constants";

const PrimaryContainer = styled.div`
  height: calc(100vh - ${appConfig.headerHeight}px);
  display: flex;
  flex-direction: column;
`;

const OverviewSection = styled.section`
  background-color: ${colours.white};
  overflow: hidden;
  color: ${colours.darkerGrey};
`;

const ToolsContainer = styled.div`
  flex: 1;
`;

const FluidContainer = styled.div`
  max-width: ${appConfig.homePageContentWidth};
  padding: 1rem;
  margin: auto;
`;

const Paragraph = styled.p`
  line-height: 26px;
`;

const Card = styled.div`
  box-sizing: border-box;
  padding: 2.1875rem 2.8125rem 2.1875rem 2.1875rem;
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${colours.th3};
  color: ${colours.th1};

  & + & {
    margin-top: 40px;
  }
`;

const CardAlt = styled(Card)`
  background-color: ${colours.th2alt};
`;

const CardLink = styled(Link)`
  --icon-offset: 0px;
  padding: 12px 0;
  font-weight: 500;
  color: ${colours.th1};
  display: inline;
  max-width: 370px;
  &:hover {
    --icon-offset: 4px;
  }
`;

const ToolTitle = styled.h3`
  font-size: 1.75rem;
  margin: 0.625rem 0;
  color: ${colours.th1};
  text-decoration: none;
`;

const StyledArrow = styled(ArrowIcon)`
  position: relative;
  top: 2px;
  width: 1.5625rem;
  height: 1rem;
  margin-left: calc(12px + var(--icon-offset));
  transition: margin-left 0.2s;
`;

const InlineTextButton = styled(TextButton)`
  display: inline-flex;
  font-size: 16px;
  font-weight: 600;
`;

const Image = (props: any) => {
  const ImageContainer = styled.div`
    margin: auto;
    height: 100%;
    width: 100%;
    flex: 1;
    background-image: url(${props.imageUrl});
    background-position: center center;
    background-repeat: no-repeat;
    background-size: cover;
    @media (max-width: 1400px) {
      background-image: none;
    }
  `;
  return <ImageContainer />;
};

const SecondaryContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
`;

const MiddleContainer = styled.div`
  padding: 1rem;
  padding-bottom: 3rem;
  position: relative;
`;

const Version = styled.div`
  position: absolute;
  bottom: 0.5rem;
  right: 2rem;
  font-size: 12px;
`;

function Home() {
  const { setDialogProps }: { setDialogProps: (props: DialogProps) => void } =
    useDialogContext();

  const onHelpClick = (title: string, helpContent: any) => {
    const onKeyDown = (e: any) => {
      if (
        ["Space", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].indexOf(
          e.code
        ) > -1
      ) {
        e.preventDefault();
      }
    };
    setDialogProps({
      open: true,
      onClose: () => {
        setDialogProps(initialDialogProps);
        window.removeEventListener("keydown", onKeyDown, false);
      },
      heading: title,
      children: helpContent,
      modal: false,
    });
    document
      .getElementById("dialog-overlay")
      ?.addEventListener("wheel", (e) => e.preventDefault());
    window.addEventListener("keydown", onKeyDown, false);
  };

  return (
    <PrimaryContainer>
      <SecondaryContainer>
        <Image imageUrl={leftImg} />

        <MiddleContainer>
          <OverviewSection>
            <FluidContainer>
              <Paragraph>
                NVR Map is an online application that underpins legislated
                native vegetation approvals in Victoria. This application allows
                users to view a range of spatial datasets relating to the Native
                Vegetation Regulations and generate Native Vegetation Removal
                Reports and Native Vegetation Offset Reports.
              </Paragraph>
              <Paragraph>
                A user guide, useful links and other information can be viewed{" "}
                <InlineTextButton
                  onClick={(e: any) =>
                    onHelpClick("NVR Map help", <LandingPageHelp />)
                  }
                >
                  here
                </InlineTextButton>
                .
              </Paragraph>
            </FluidContainer>
          </OverviewSection>

          <ToolsContainer>
            <FluidContainer>
              <CardAlt>
                <ToolTitle>Native vegetation offset tool</ToolTitle>
                <p>
                  The native vegetation offset tool is used to generate a Native
                  Vegetation Offset Report, which is required to establish a
                  first-party general offset site. This specific type of offset
                  can only be secured on a permit holder’s own property to meet
                  their own general offset requirements. The{" "}
                  <LinkComponent
                    href="https://www.environment.vic.gov.au/native-vegetation/native-vegetation-removal-regulations"
                    target="_blank"
                    title="DEECA Native Vegetation Regulation website"
                    rel="noopener noreferrer"
                  >
                    DEECA Native Vegetation Regulation website
                  </LinkComponent>
                  <ExternalLinkIcon /> outlines the process required for
                  establishing a third-party offset site and first-party offset
                  site for species offset requirements.
                </p>
                <CardLink to="/offset">
                  Go to the native vegetation offset tool
                  <StyledArrow />
                </CardLink>
              </CardAlt>
              <Card>
                <ToolTitle>Native vegetation removal tool</ToolTitle>
                <p>
                  The native vegetation removal tool is used to generate a
                  Native Vegetation Removal Report, which is required to support
                  an application for approval to remove, destroy or lop native
                  vegetation. The tool will determine the applicable Assessment
                  Pathway and generate a report for proposals in the Basic or
                  Intermediate Assessment Pathway. The{" "}
                  <LinkComponent
                    href={applicantsGuide}
                    target="_blank"
                    title="Applicant’s Guide"
                    rel="noopener noreferrer"
                  >
                    Applicant’s Guide
                  </LinkComponent>
                  <ExternalLinkIcon /> outlines the process required for
                  proposals in the Detailed Assessment Pathway.
                </p>
                <CardLink to="/removal">
                  Go to the native vegetation removal tool
                  <StyledArrow />
                </CardLink>
              </Card>
            </FluidContainer>
          </ToolsContainer>

          <Version>version {appConfig.version}</Version>
        </MiddleContainer>

        <Image imageUrl={rightImg} />
      </SecondaryContainer>

      <Footer />
      <DialogContainer />
    </PrimaryContainer>
  );
}

function LandingPage() {
  return (
    <ContextProvider>
      <Home />
    </ContextProvider>
  );
}

export default LandingPage;
